import {
  Control,
  Controller,
  FieldErrorsImpl,
  UseFormRegister,
} from "react-hook-form";

import { Input, Select } from "components/common/basic";
import {
  BankAccountFragment,
  FreelanceProfileFragment,
} from "graphql/fragments";
import { BankAccountAttributes, CurrencyEnum } from "graphql/types";
import { Trans, useTranslation } from "translations";
import { tw } from "utils/tw";

// The API accepts a bank account without a currency; these forms always ask
// for one, so the form value carries it as required.
export interface BankAccountFormValues extends BankAccountAttributes {
  currency: CurrencyEnum;
}

export interface Props {
  control: Control<FreelanceProfileFragment>;
  register: UseFormRegister<FreelanceProfileFragment>;
  triggerBankAccountValidation: () => void;
  bankAccount?: BankAccountFragment;
  bankAccountErrors?: FieldErrorsImpl<BankAccountFragment>;
}

export default ({
  control,
  register,
  triggerBankAccountValidation,
  bankAccount,
  bankAccountErrors,
}: Props): React.JSX.Element => {
  const { t } = useTranslation("common");

  return (
    <div className={tw("w-full", "space-y-4")}>
      <h2 className={tw("text-lg", "font-bold")}>
        <Trans
          ns="account"
          i18nKey="freelanceProfileForm.bankAccountSection.title"
        >
          Bank account information
        </Trans>
      </h2>

      <div className={tw("flex", "space-x-4")}>
        <Input
          className={tw("w-3/4")}
          id="accountNumber"
          {...register("defaultBankAccount.accountNumber")}
          defaultValue={bankAccount?.accountNumber}
          label={t(
            "freelanceProfileForm.bankAccount.accountNumber.label",
            "Account number"
          )}
          placeholder={t(
            "freelanceProfileForm.bankAccount.accountNumber.placeholder",
            "Number"
          )}
          errorMessage={bankAccountErrors?.accountNumber?.message}
        />

        <Controller
          control={control}
          name="defaultBankAccount.currency"
          defaultValue={bankAccount?.currency ?? CurrencyEnum.Nok}
          render={({ field }) => (
            <Select
              className={tw("w-1/4")}
              id={field.name}
              name={field.name}
              label={t(
                "freelanceProfileForm.bankAccount.currency.label",
                "Currency"
              )}
              options={Object.values(CurrencyEnum).map((value) => ({
                label: value,
                value,
              }))}
              value={field.value ?? undefined}
              onChange={(event) => {
                field.onChange(event);

                triggerBankAccountValidation();
              }}
              errorMessage={bankAccountErrors?.currency?.message}
            />
          )}
        />
      </div>

      <Input
        id="swiftCode"
        {...register("defaultBankAccount.swiftCode")}
        defaultValue={bankAccount?.swiftCode ?? ""}
        label={t(
          "freelanceProfileForm.bankAccount.swiftCode.label",
          "BIC / SWIFT"
        )}
        placeholder={t(
          "freelanceProfileForm.bankAccount.swiftCode.placeholder",
          "Number"
        )}
        hint={t("freelanceProfileForm.bankAccount.swiftCode.hint", "Optional")}
        errorMessage={bankAccountErrors?.swiftCode?.message}
      />
    </div>
  );
};
